import { useNuxtApp } from 'nuxt/app'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import { getApiBaseUrl } from '~/configs/environments/config'
import type { Token } from '~/api/modules/starters/auth/types'

export interface ApiErrorResponse {
  message?: string
  [key: string]: unknown
}

type ApiFetchOptions = NitroFetchOptions<NitroFetchRequest> & {
  enableGlobalError?: boolean
}
type ApiMethod = NonNullable<ApiFetchOptions['method']>

export interface ApiClient {
  request<T>(url: string, options?: ApiFetchOptions): Promise<T>
  get<T>(url: string, options?: ApiFetchOptions): Promise<T>
  post<T>(url: string, body?: unknown, options?: ApiFetchOptions): Promise<T>
  put<T>(url: string, body?: unknown, options?: ApiFetchOptions): Promise<T>
  patch<T>(url: string, body?: unknown, options?: ApiFetchOptions): Promise<T>
  delete<T>(url: string, options?: ApiFetchOptions): Promise<T>
}

const withBody = (
  body: unknown,
  options?: ApiFetchOptions
): ApiFetchOptions | undefined => {
  if (typeof body === 'undefined') {
    return options
  }

  return {
    ...(options ?? {}),
    body
  }
}

const AUTH_STORAGE_KEY = 'dphub/auth'

const createHeaders = ({
  defaults,
  optionHeaders,
  language
}: {
  defaults: Headers
  optionHeaders?: HeadersInit
  language?: string
}) => {
  const headers = new Headers(defaults)

  if (language) {
    headers.set('accept-language', language)
  }

  if (optionHeaders) {
    const customHeaders = new Headers(optionHeaders)
    customHeaders.forEach((value, key) => {
      headers.set(key, value)
    })
  }

  return headers
}

type AuthStorage = {
  pass_token?: Token | null
  [key: string]: unknown
}

const readAuthStorage = (): AuthStorage | null => {
  if (import.meta.server) return null

  const raw = localStorage.getItem(AUTH_STORAGE_KEY)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const writeAuthStorage = (value: AuthStorage | null) => {
  if (import.meta.server) return null

  if (!value) {
    localStorage.removeItem(AUTH_STORAGE_KEY)

    return
  }

  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(value))
}

const getAccessToken = () => readAuthStorage()?.pass_token?.access_token ?? null

const syncAuthorizationHeader = (headers: Headers) => {
  const authToken = getAccessToken()

  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`)
  } else {
    headers.delete('Authorization')
  }
}

const pickLanguage = (
  nuxtApp: ReturnType<typeof useNuxtApp>
): string | undefined => {
  const i18n = (nuxtApp as unknown as { $i18n?: any }).$i18n

  if (!i18n) {
    return undefined
  }

  const locale = i18n.locale

  if (!locale) {
    return undefined
  }

  if (typeof locale === 'string') {
    return locale
  }

  if (typeof locale === 'object' && 'value' in locale) {
    return locale.value
  }

  return undefined
}

export const createApiClient = async (): Promise<ApiClient> => {
  const host = await getApiBaseUrl()
  const nuxtApp = useNuxtApp()
  const defaultHeaders = new Headers({})
  syncAuthorizationHeader(defaultHeaders)

  const client = $fetch.create({
    baseURL: host
  })

  let refreshPromise: Promise<string | null> | null = null

  const refreshAccessToken = async (): Promise<string | null> => {
    if (refreshPromise) {
      return refreshPromise
    }

    const refreshToken = readAuthStorage()?.pass_token?.refresh_token

    if (!refreshToken) {
      return null
    }

    const language = pickLanguage(nuxtApp)

    refreshPromise = client<Token>('/api/v1/auth/refresh-token', {
      method: 'POST',
      body: {
        refresh_token: refreshToken
      },
      headers: createHeaders({
        defaults: new Headers({}),
        language
      })
    })
      .then(token => {
        const authData = readAuthStorage() ?? {}
        authData.pass_token = {
          ...(authData.pass_token ?? {}),
          ...token
        }
        writeAuthStorage(authData)
        syncAuthorizationHeader(defaultHeaders)

        return token.access_token
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null
      })

    return refreshPromise
  }

  const request = async <T>(url: string, options?: ApiFetchOptions) => {
    const {
      headers: optionHeaders,
      onRequest,
      onRequestError,
      onResponse,
      onResponseError,
      enableGlobalError,
      ...rest
    } = options ?? {}

    const language = pickLanguage(nuxtApp)
    let refreshAttempted = false

    const snacks = (
      nuxtApp as unknown as { $snacks?: { error: (message: string) => void } }
    ).$snacks

    const makeRequest = async (): Promise<T> => {
      syncAuthorizationHeader(defaultHeaders)
      let shouldRetryAfterRefresh = false

      try {
        return await client<T>(url, {
          ...rest,
          headers: createHeaders({
            defaults: defaultHeaders,
            optionHeaders,
            language
          }),
          async onRequest(ctx) {
            await onRequest?.(ctx)
          },
          async onRequestError(ctx) {
            await onRequestError?.(ctx)
          },
          async onResponse(ctx) {
            await onResponse?.(ctx)
          },
          async onResponseError(ctx) {
            if (ctx.response?.status === 401 && !refreshAttempted) {
              refreshAttempted = true
              const refreshedToken = await refreshAccessToken()

              if (refreshedToken) {
                shouldRetryAfterRefresh = true

                return
              }
            }

            if (enableGlobalError && snacks?.error) {
              const errorData = (ctx.response as { _data?: ApiErrorResponse })
                ?._data
              const errorMessage = errorData?.message || 'Произошла ошибка'
              snacks.error(errorMessage)
            }

            await onResponseError?.(ctx)
          }
        })
      } catch (error) {
        if (shouldRetryAfterRefresh) {
          return makeRequest()
        }

        throw error
      }
    }

    return makeRequest()
  }

  const withMethod = <T>(
    method: ApiMethod,
    url: string,
    options?: ApiFetchOptions
  ) => {
    return request<T>(url, {
      ...options,
      method
    })
  }

  return {
    request,
    get: (url, options) => withMethod('get', url, options),
    post: (url, body, options) =>
      withMethod('post', url, withBody(body, options)),
    put: (url, body, options) =>
      withMethod('put', url, withBody(body, options)),
    patch: (url, body, options) =>
      withMethod('patch', url, withBody(body, options)),
    delete: (url, options) => withMethod('delete', url, options)
  }
}

export const useApiClient = async (): Promise<ApiClient> => {
  return await createApiClient()
}

declare module '#app' {
  interface NuxtApp {
    _apiClient?: ApiClient
  }
}

declare module 'nuxt/app' {
  interface NuxtApp {
    _apiClient?: ApiClient
  }
}
